import { getCssToken } from './getCssToken';
import { LIGHT_HEX, TOKEN_CSS_VARS } from './tokens';

function applyOpacity(hex: string, opacity: number): string {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return hex;
  const r = Number.parseInt(normalized.slice(0, 2), 16);
  const g = Number.parseInt(normalized.slice(2, 4), 16);
  const b = Number.parseInt(normalized.slice(4, 6), 16);
  if ([r, g, b].some((channel) => Number.isNaN(channel))) return hex;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export function isChartDarkTheme(): boolean {
  return typeof document !== 'undefined' && document.documentElement.dataset.theme === 'dark';
}

/** Gate force keeps its own lighter grid in light mode; uses chart-grid in dark. */
export function getGateForceGridChrome(): { stroke: string; opacity: number } {
  if (isChartDarkTheme()) {
    return {
      stroke: getCssToken(TOKEN_CSS_VARS.chart.grid, '#484848'),
      opacity: 1,
    };
  }
  return { stroke: '#EEF2F7', opacity: 0.9 };
}

export function getChartChrome() {
  const dark = isChartDarkTheme();
  return {
    axisTick: getCssToken(TOKEN_CSS_VARS.gray[500], LIGHT_HEX.gray[500]),
    axisLabel: getCssToken(TOKEN_CSS_VARS.gray[700], LIGHT_HEX.gray[700]),
    axisLine: getCssToken(TOKEN_CSS_VARS.gray[300], LIGHT_HEX.gray[300]),
    grid: dark
      ? getCssToken(TOKEN_CSS_VARS.chart.grid, '#484848')
      : getCssToken(TOKEN_CSS_VARS.gray[200], LIGHT_HEX.gray[200]),
    tooltipBg: getCssToken(TOKEN_CSS_VARS.gray[900], LIGHT_HEX.gray[900]),
    tooltipFg: getCssToken(TOKEN_CSS_VARS.chart.tooltipFg, '#FFFFFF'),
    barTrack: getCssToken(TOKEN_CSS_VARS.chart.barTrack, '#F8FAFC'),
    barRange: getCssToken(TOKEN_CSS_VARS.chart.barRange, '#CBD5E1'),
    barPercentile: getCssToken(TOKEN_CSS_VARS.chart.barPercentile, '#64748B'),
    overlayPeaks: getCssToken(TOKEN_CSS_VARS.chart.overlayPeaks, '#DC2626'),
    overlayDensity: getCssToken(TOKEN_CSS_VARS.chart.overlayDensity, '#F59E0B'),
    peakStroke: getCssToken(TOKEN_CSS_VARS.surface, LIGHT_HEX.surface),
  };
}

export type ArcSegmentKey = 'catchSlip' | 'effectiveLength' | 'finishSlip';

export interface ArcSegmentPaint {
  fill: string;
  stroke: string;
}

const ARC_SEGMENT_TOKEN: Record<ArcSegmentKey, string> = {
  catchSlip: '--rowing-red',
  effectiveLength: '--rowing-green',
  finishSlip: '--rowing-yellow',
};

const ARC_SEGMENT_FALLBACK: Record<ArcSegmentKey, string> = {
  catchSlip: '#DC2626',
  effectiveLength: '#16A34A',
  finishSlip: '#EAB308',
};

/** Per-segment fill/stroke tuned for light vs dark canvas. */
export function getArcSegmentPaint(): Record<ArcSegmentKey, ArcSegmentPaint> {
  const dark = isChartDarkTheme();
  const base = (key: ArcSegmentKey) =>
    getCssToken(ARC_SEGMENT_TOKEN[key], ARC_SEGMENT_FALLBACK[key]);

  if (!dark) {
    return {
      catchSlip: { fill: applyOpacity(base('catchSlip'), 0.85), stroke: '#FFFFFF' },
      effectiveLength: { fill: applyOpacity(base('effectiveLength'), 0.9), stroke: '#FFFFFF' },
      finishSlip: { fill: applyOpacity(base('finishSlip'), 0.9), stroke: '#FFFFFF' },
    };
  }

  // Dark: readable fills on charcoal canvas — matched to legend swatch brightness.
  return {
    catchSlip: {
      fill: applyOpacity(base('catchSlip'), 0.58),
      stroke: applyOpacity(base('catchSlip'), 0.92),
    },
    effectiveLength: {
      fill: applyOpacity(base('effectiveLength'), 0.62),
      stroke: applyOpacity(base('effectiveLength'), 0.95),
    },
    finishSlip: {
      fill: applyOpacity(base('finishSlip'), 0.58),
      stroke: applyOpacity(base('finishSlip'), 0.92),
    },
  };
}

const SEGMENT_ID_TO_KEY: Record<string, ArcSegmentKey> = {
  'catch-slip': 'catchSlip',
  'effective-length': 'effectiveLength',
  'finish-slip': 'finishSlip',
};

export function getArcSegmentPaintById(segmentId: string): ArcSegmentPaint | undefined {
  const key = SEGMENT_ID_TO_KEY[segmentId];
  return key ? getArcSegmentPaint()[key] : undefined;
}

/** Rowing arc diagram — segment fills, oar, and legend chrome that must track theme. */
export function getArcChartChrome() {
  const dark = isChartDarkTheme();
  const chrome = getChartChrome();
  const segments = getArcSegmentPaint();

  return {
    ...chrome,
    segments,
    catchSlip: getCssToken('--rowing-red', '#DC2626'),
    effectiveLength: getCssToken('--rowing-green', '#16A34A'),
    finishSlip: getCssToken('--rowing-yellow', '#EAB308'),
    referenceLine: dark
      ? getCssToken(TOKEN_CSS_VARS.chart.grid, '#484848')
      : 'rgba(55, 65, 81, 0.35)',
    oarShaft: dark ? getCssToken(TOKEN_CSS_VARS.gray[600], '#D4D4D4') : '#0B1120',
    oarPivot: dark ? getCssToken(TOKEN_CSS_VARS.gray[500], LIGHT_HEX.gray[500]) : '#0F172A',
    totalArc: dark ? getCssToken('--blue-500', '#60A5FA') : '#1E40AF',
    totalArcOpacity: dark ? 0.45 : 0.65,
    inactiveSegmentOpacity: dark ? 0.5 : 0.35,
    segmentStrokeWidth: dark ? 1.5 : 2,
    segmentActiveStrokeWidth: dark ? 2.25 : 3,
    legendInactiveOpacity: dark ? 0.65 : 0.45,
    tooltipBg: dark ? getCssToken('--chart-tooltip-bg', '#262626') : 'rgba(0, 0, 0, 0.8)',
    tooltipFg: getCssToken('--chart-tooltip-fg', '#FFFFFF'),
  };
}
